import { useState } from 'react';
import { IS_PROD, PREVIEW_SURFACE_OVERRIDE } from '../lib/subdomains.js';

const IS_PREVIEW_ENV = !IS_PROD && !window.location.hostname.includes('localhost');

const SURFACES = [
  { key: null, label: 'Main Site', desc: 'daulton.co' },
  { key: 'admin', label: 'Admin', desc: 'admin.daulton.co' },
  { key: 'brief', label: 'Morning Brief', desc: 'brief.daulton.co' },
  { key: 'wexford', label: 'Wexford Reach', desc: 'wexford.daulton.co' },
  { key: 'teldire', label: 'Teldire', desc: 'teldire.daulton.co' },
];

function applySurface(key) {
  // Store 'main' as a sentinel so "no selection yet" (missing key) stays
  // distinguishable from "user explicitly chose the main site".
  sessionStorage.setItem('_previewSurface', key ?? 'main');
  window.location.href = '/';
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 99999,
    background: 'rgba(15, 12, 9, 0.92)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: '"Lora", "Libre Baskerville", Georgia, serif',
    padding: '24px',
  },
  heading: {
    color: '#f0e8dc',
    fontSize: '1.125rem',
    fontWeight: 600,
    marginBottom: '8px',
    letterSpacing: '0.01em',
  },
  subheading: {
    color: '#9c8e7e',
    fontSize: '0.8125rem',
    marginBottom: '32px',
    fontStyle: 'italic',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    width: '100%',
    maxWidth: '380px',
  },
  surfaceBtn: {
    background: 'rgba(240, 232, 220, 0.06)',
    border: '1px solid rgba(240, 232, 220, 0.15)',
    borderRadius: '8px',
    padding: '16px 12px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background 0.15s, border-color 0.15s',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  surfaceBtnLabel: {
    color: '#f0e8dc',
    fontSize: '0.9375rem',
    fontWeight: 600,
  },
  surfaceBtnDesc: {
    color: '#6b5f52',
    fontSize: '0.75rem',
    fontFamily: 'ui-monospace, "Fira Code", monospace',
  },
  badge: {
    position: 'fixed',
    bottom: '12px',
    right: '12px',
    zIndex: 99999,
    background: 'rgba(15, 12, 9, 0.85)',
    border: '1px solid rgba(240, 232, 220, 0.18)',
    borderRadius: '20px',
    padding: '6px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: 'ui-monospace, "Fira Code", monospace',
    fontSize: '0.75rem',
    backdropFilter: 'blur(8px)',
  },
  badgeLabel: {
    color: '#9c8e7e',
  },
  badgeSurface: {
    color: '#f0e8dc',
    fontWeight: 600,
  },
  badgeBtn: {
    background: 'none',
    border: 'none',
    color: '#7c9fbd',
    cursor: 'pointer',
    fontSize: '0.75rem',
    padding: 0,
    fontFamily: 'inherit',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  },
};

export default function PreviewSurfacePicker() {
  const [showPicker, setShowPicker] = useState(!PREVIEW_SURFACE_OVERRIDE);

  if (!IS_PREVIEW_ENV) return null;

  const overrideKey = PREVIEW_SURFACE_OVERRIDE === 'main' ? null : PREVIEW_SURFACE_OVERRIDE;
  const currentSurface = SURFACES.find(s => s.key === overrideKey) ?? SURFACES[0];

  if (!showPicker) {
    return (
      <div style={styles.badge}>
        <span style={styles.badgeLabel}>preview:</span>
        <span style={styles.badgeSurface}>{currentSurface.label}</span>
        <button style={styles.badgeBtn} onClick={() => setShowPicker(true)}>
          switch
        </button>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <p style={styles.heading}>Which surface do you want to preview?</p>
      <p style={styles.subheading}>Selection persists for this browser tab.</p>
      <div style={styles.grid}>
        {SURFACES.map(s => (
          <button
            key={String(s.key)}
            style={styles.surfaceBtn}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(240, 232, 220, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(240, 232, 220, 0.3)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'rgba(240, 232, 220, 0.06)';
              e.currentTarget.style.borderColor = 'rgba(240, 232, 220, 0.15)';
            }}
            onClick={() => applySurface(s.key)}
          >
            <span style={styles.surfaceBtnLabel}>{s.label}</span>
            <span style={styles.surfaceBtnDesc}>{s.desc}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
